import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Camera, LogOut, Save, KeyRound, Music, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { LoadingState, UnauthorizedState } from '@/components/states';
import { AccountPrivacySection } from '@/components/account/AccountPrivacySection';

export default function Account() {
  const { session, profile, refreshProfile, setProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ display_name: '', username: '', bio: '' });
  const [pwd, setPwd] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!session) return;
    api.get('/me').then(({ data }) => {
      setMe(data);
      setForm({ display_name: data.display_name || '', username: data.username || '', bio: data.bio || '' });
    });
  }, [session, profile]);

  if (!session) return <UnauthorizedState />;
  if (!me) return <LoadingState />;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/me', form);
      setProfile((p) => ({ ...p, ...data }));
      toast.success('Profil mis à jour');
      refreshProfile();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post('/me/avatar', { data_url: reader.result });
        setProfile((p) => ({ ...p, avatar_url: data.avatar_url }));
        setMe((m) => ({ ...m, avatar_url: data.avatar_url }));
        toast.success('Avatar mis à jour');
      } catch (err) { toast.error(apiError(err)); }
    };
    reader.readAsDataURL(file);
  };

  const changePwd = async () => {
    if (pwd.length < 6) { toast.error('Mot de passe trop court (min 6).'); return; }
    const { error } = await supabase.auth.updateUser({ password: pwd });
    if (error) toast.error(error.message);
    else { toast.success('Mot de passe modifié'); setPwd(''); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div className="card-surface relative overflow-hidden p-6">
        <div className="absolute inset-0 bg-gradient-to-br from-brand/15 to-transparent" />
        <div className="relative flex flex-col items-center gap-4 sm:flex-row">
          <div className="relative">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl bg-surface-cardHover text-3xl font-bold text-white">
              {me.avatar_url ? <img src={me.avatar_url} alt="" className="h-full w-full object-cover" /> : (me.display_name?.[0] || 'U').toUpperCase()}
            </div>
            <button onClick={() => fileRef.current?.click()} data-testid="avatar-upload-button"
                    className="absolute -bottom-2 -right-2 flex h-9 w-9 items-center justify-center rounded-full bg-brand text-black btn-glow">
              <Camera className="h-4 w-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="avatar-input" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h1 className="font-display text-2xl font-bold text-white">{me.display_name || 'Artiste'}</h1>
            <p className="text-muted-foreground">@{me.username}</p>
            <p className="text-sm text-muted-foreground">{me.email}</p>
          </div>
          <div className="flex gap-6">
            <Stat icon={Music} value={me.stats?.songs ?? 0} label="Créations" />
            <Stat icon={Trophy} value={me.stats?.challenge_entries ?? 0} label="Challenges" />
          </div>
        </div>
      </div>

      <section className="card-surface space-y-4 p-6">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><User className="h-5 w-5 text-brand" /> Informations</h2>
        <F label="Pseudo"><input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="account-username" className={inp} /></F>
        <F label="Nom affiché"><input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="account-displayname" className={inp} /></F>
        <F label="Bio"><textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} data-testid="account-bio" className={`${inp} resize-none`} /></F>
        <button onClick={save} disabled={saving} data-testid="account-save"
                className="flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 font-semibold text-black btn-glow disabled:opacity-60">
          <Save className="h-4 w-4" /> Enregistrer
        </button>
      </section>

      <section className="card-surface space-y-4 p-6">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><KeyRound className="h-5 w-5 text-brand" /> Mot de passe</h2>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Nouveau mot de passe" data-testid="account-password" className={`${inp} flex-1`} />
          <button onClick={changePwd} className="rounded-full bg-surface-cardHover px-6 py-2.5 font-semibold text-white hover:bg-white/10" data-testid="account-change-password">Modifier</button>
        </div>
      </section>

      <AccountPrivacySection />

      <button onClick={() => { signOut(); navigate('/'); }} data-testid="account-logout"
              className="flex items-center gap-2 rounded-full bg-destructive/15 px-6 py-2.5 font-semibold text-destructive hover:bg-destructive/25">
        <LogOut className="h-4 w-4" /> Se déconnecter
      </button>
    </div>
  );
}

const inp = 'w-full rounded-xl border border-border bg-surface-input px-4 py-2.5 text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none';
function F({ label, children }) {
  return <label className="block space-y-1.5"><span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{label}</span>{children}</label>;
}
function Stat({ icon: Icon, value, label }) {
  return (
    <div className="text-center">
      <p className="flex items-center justify-center gap-1 font-display text-2xl font-bold text-brand"><Icon className="h-4 w-4" />{value}</p>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}
